import i18n from '@/i18n'
import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAgency } from '@/contexts/AgencyContext'
import type { Database } from '@/lib/supabase'
import {
  saveUserSession,
  getUserSession,
  clearUserSession,
  validateSessionAgency,
  updateLastActivity,
  shouldRefreshSession,
  extendUserSession,
  type AuthUser
} from '@/utils/sessionStorage'
import { setUserContext, clearUserContext } from '@/lib/sentry'

interface UseAgencyAuthReturn {
  user: AuthUser | null
  loading: boolean
  error: string | null
  login: (email: string, password: string) => Promise<boolean>
  logout: () => Promise<void>
  isAuthenticated: boolean
}

export const useAgencyAuth = (): UseAgencyAuthReturn => {
  const { agency } = useAgency()
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  /**
   * Verifica e restaura sessão usando Supabase Auth nativo
   */
  const checkStoredAuth = async (): Promise<void> => {
    const timeoutDuration = 8000 // 8 segundos
    const timeoutPromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        console.warn('⚠️ [Auth] Verificação de sessão excedeu o tempo limite')
        setLoading(false)
        resolve()
      }, timeoutDuration)
    })

    const authCheckPromise = (async () => {
      try {
        console.log('🔍 [Auth] Verificando sessão armazenada...')

        if (!agency) {
          console.log('⚠️ [Auth] Agência não carregada ainda')
          setLoading(false)
          return
        }

        if (!supabase) {
          setError(i18n.t('common:errors.notConfigured'))
          setLoading(false)
          return
        }

        console.log('🔐 [Auth] Verificando sessão no Supabase Auth...')

        // Obter sessão atual do Supabase Auth
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()

      if (sessionError) {
        console.log('❌ [Auth] Erro ao obter sessão:', sessionError.message)
        clearUserSession()
        setLoading(false)
        return
      }

      if (!session) {
        console.log('❌ [Auth] Nenhuma sessão ativa no Supabase Auth')
        const localSession = getUserSession()
        if (localSession) {
          console.log('⚠️ [Auth] Sessão local encontrada mas Supabase inválido, limpando...')
          clearUserSession()
        }
        setLoading(false)
        return
      }

      console.log('✅ [Auth] Sessão ativa encontrada no Supabase Auth:', {
        user_id: session.user.id,
        email: session.user.email,
        expires_at: session.expires_at,
        access_token_preview: session.access_token?.substring(0, 20) + '...'
      })

      // Verificar se auth.uid() está disponível
      const { data: { user: authUser }, error: authUserError } = await supabase.auth.getUser()
      console.log('🔍 [Auth] Verificando auth.uid():', {
        auth_uid_available: !!authUser,
        auth_uid: authUser?.id,
        matches_session: authUser?.id === session.user.id,
        error: authUserError?.message
      })

      // Buscar dados completos do usuário no banco (já filtra por agency_id internamente)
      const fetchResult = await fetchUserData(session.user.id, session.user.email || '')

      if (!fetchResult) {
        console.log('❌ [Auth] Usuário não encontrado ou não pertence a esta agência')
        clearUserSession()
        setLoading(false)
        return
      }

      const { userData, availableAccounts } = fetchResult

      const userAgencyId = userData.type === 'agency_user' ? (userData as any).agency_id : userData.account?.agency_id
      const isAgencyMatch = userAgencyId === agency.id

      console.log('✅ [Auth] Usuário autenticado automaticamente:', {
        user_id: userData.id,
        user_name: userData.name,
        user_email: userData.email,
        auth_user_id: userData.auth_user_id,
        account_id: userData.account_id,
        user_agency_id: userAgencyId,
        current_agency: agency.company_name,
        current_agency_id: agency.id,
        domain: agency.domain,
        type: userData.type,
        agency_match: isAgencyMatch,
        status: isAgencyMatch ? '✅ AGÊNCIA CORRESPONDENTE' : '⚠️ AGÊNCIA DIFERENTE',
        available_accounts: availableAccounts?.length || 0
      })

      const localSession = getUserSession()

      if (userData.type === 'agency_user') {
        // Sem conta preferida no localStorage, mantém a conta padrão resolvida pelo
        // fetchUserData. A sessão RLS é recriada de qualquer forma: ela expira em
        // 24h e, sem ela, get_auth_account_id() não resolve a conta do agency_user.
        if (localSession?.user?.account_id) {
          console.log('🔐 [Auth] Restaurando sessão RLS para agency_user...')
          userData.account_id = localSession.user.account_id
          userData.account = localSession.user.account
        } else {
          console.log('🔐 [Auth] Criando sessão RLS para agency_user na conta padrão...')
        }

        try {
          const { error: sessionError } = await supabase.rpc('update_agency_user_session', {
            p_account_id: userData.account_id
          })

          if (sessionError) {
            console.error('❌ [Auth] Erro ao restaurar sessão RLS:', sessionError)
          } else {
            console.log('✅ [Auth] Sessão RLS restaurada para account_id:', userData.account_id)
          }
        } catch (err) {
          console.error('❌ [Auth] Erro ao restaurar sessão RLS:', err)
        }
      }

      if (userData.type === 'account_user' && localSession?.user?.account_id && localSession.user.account_id !== userData.account_id) {
        const storedAccountId = localSession.user.account_id
        const storedAvailable = localSession.availableAccounts || []
        const matchesAvailable = storedAvailable.some((acc: any) => acc.id === storedAccountId)

        if (matchesAvailable && localSession.user.id && localSession.user.email) {
          console.log('🔐 [Auth] Restaurando conta preferida para account_user:', storedAccountId)
          userData.id = localSession.user.id
          userData.email = localSession.user.email
          userData.name = localSession.user.name
          userData.phone = localSession.user.phone
          userData.profile = localSession.user.profile
          userData.account_id = localSession.user.account_id
          userData.account = localSession.user.account
          userData.role = localSession.user.role
          userData.view_all_contacts = localSession.user.view_all_contacts
          userData.can_assign_contacts = localSession.user.can_assign_contacts
          userData.view_unassigned_contacts = localSession.user.view_unassigned_contacts

          try {
            const { error: rpcError } = await supabase.rpc('set_active_account', {
              p_account_id: storedAccountId
            })
            if (rpcError) {
              console.error('❌ [Auth] Erro ao sincronizar conta ativa no banco:', rpcError)
            }
          } catch (err) {
            console.error('❌ [Auth] Erro ao sincronizar conta ativa no banco:', err)
          }
        }
      }

      setUser(userData)

      // Identificar usuário no Sentry
      setUserContext({
        id: userData.id,
        email: userData.email,
        name: userData.name,
        accountId: userData.account_id,
        profile: userData.profile
      })

      // Salvar sessão completa no localStorage
      const supabaseSession = {
        access_token: session.access_token,
        refresh_token: session.refresh_token,
        expires_in: session.expires_in || 3600,
        expires_at: session.expires_at || Math.floor(Date.now() / 1000) + 3600
      }
      saveUserSession(userData, session.access_token, agency.id, supabaseSession, availableAccounts)
      setError(null)

      console.log('=' .repeat(80))
      console.log('🔐 SESSÃO VERIFICADA - USUÁRIO AUTENTICADO')
      console.log('=' .repeat(80))
      console.log('✅ Sessão válida do Supabase Auth restaurada')
      console.log('')
      console.log('👤 Usuário Logado:')
      console.log('   - ID:', userData.id)
      console.log('   - Nome:', userData.name)
      console.log('   - Email:', userData.email)
      console.log('   - Account ID:', userData.account_id)
      console.log('')
      console.log('🏢 Agência:')
      console.log('   - Nome:', agency.company_name)
      console.log('   - Domínio:', agency.domain)
      console.log('')
      console.log('=' .repeat(80))

      } catch (err) {
        console.error('💥 [Auth] Erro inesperado ao verificar sessão:', err)
        setError(`Erro ao verificar autenticação: ${err instanceof Error ? err.message : 'Erro desconhecido'}`)
        clearUserSession()
      } finally {
        setLoading(false)
      }
    })()

    // Executar com timeout
    await Promise.race([authCheckPromise, timeoutPromise])
  }

  /**
   * Busca dados completos do usuário após login
   * PRIORIDADE 1: agency_users (com filtro de agency_id)
   * PRIORIDADE 2: account_users (com filtro de agency_id via accounts)
   * Retorna: { userData, availableAccounts }
   */
  const fetchUserData = async (authUserId: string, email: string): Promise<{ userData: AuthUser; availableAccounts?: any[] } | null> => {
    if (!supabase) return null
    if (!agency) {
      console.error('❌ [Auth] Agência não disponível para validação')
      return null
    }

    try {
      // PRIORIDADE 1: Buscar em agency_users primeiro (com filtro de agency_id)
      console.log('🔍 [Auth] [PRIORIDADE 1] Buscando usuário em agency_users...')
      console.log('   Filtros: auth_user_id =', authUserId, '+ agency_id =', agency.id)

      const { data: agencyUser, error: agencyError } = await supabase
        .from('agency_users')
        .select(`
          id,
          agency_id
        `)
        .eq('auth_user_id', authUserId)
        .eq('agency_id', agency.id)
        .maybeSingle()

      if (agencyError) {
        console.warn('⚠️ [Auth] Erro ao buscar em agency_users:', agencyError.message)
      }

      if (agencyUser) {
        console.log('✅ [Auth] Usuário encontrado em agency_users')
        console.log('   agency_id validado:', agencyUser.agency_id, '=', agency.id)

        // Buscar dados frescos do auth.users (fonte primária de verdade)
        const { data: { user: authUserData } } = await supabase.auth.getUser()

        const displayEmail = authUserData?.email || email
        const displayName = authUserData?.user_metadata?.name ||
                           authUserData?.user_metadata?.full_name ||
                           displayEmail.split('@')[0] ||
                           'Agency User'

        console.log('📧 [Auth] Dados do auth.users:', {
          auth_email: authUserData?.email,
          auth_name: authUserData?.user_metadata?.name,
          using_email: displayEmail,
          using_name: displayName
        })

        // Para agency_users, buscar a primeira conta ativa da agência como padrão
        const { data: firstAccount } = await supabase
          .from('accounts')
          .select('*')
          .eq('agency_id', agencyUser.agency_id)
          .eq('is_active', true)
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle()

        if (!firstAccount) {
          console.error('❌ [Auth] Nenhuma conta encontrada para a agência')
          return null
        }

        const userData = {
          id: agencyUser.id,
          email: displayEmail,
          name: displayName,
          phone: '',
          profile: 'agency',
          account_id: firstAccount.id,
          account: firstAccount as any,
          auth_user_id: authUserId,
          type: 'agency_user' as const,
          agency_id: agencyUser.agency_id
        }

        console.log('✅ [Auth] Dados do agency_user preparados:', {
          id: userData.id,
          email: userData.email,
          name: userData.name,
          type: userData.type,
          profile: userData.profile,
          account_id: userData.account_id,
          agency_id: userData.agency_id
        })

        return { userData, availableAccounts: undefined }
      }

      // PRIORIDADE 2: Se não encontrou em agency_users, buscar em account_users
      console.log('🔍 [Auth] [PRIORIDADE 2] Usuário não encontrado em agency_users')
      console.log('   Buscando TODAS as contas do usuário em account_users...')
      console.log('   Filtro: auth_user_id =', authUserId)

      const { data: accountUsers, error: accountError } = await supabase
        .from('account_users')
        .select(`
          id,
          email,
          name,
          phone,
          profile,
          account_id,
          role,
          view_all_contacts,
          can_assign_contacts,
          view_unassigned_contacts,
          is_active,
          created_at,
          account:accounts!account_users_account_id_fkey (
            id,
            nome,
            agency_id,
            license_id,
            email,
            phone,
            is_active,
            created_at
          )
        `)
        .eq('auth_user_id', authUserId)
        .eq('is_active', true)
        .order('created_at', { ascending: true })

      if (accountError) {
        console.warn('⚠️ [Auth] Erro ao buscar em account_users:', accountError.message)
      }

      if (!accountUsers || accountUsers.length === 0) {
        console.log('❌ [Auth] Usuário não encontrado em account_users')
        console.log('   auth_user_id:', authUserId)
        return null
      }

      console.log(`✅ [Auth] Encontradas ${accountUsers.length} conta(s) para o usuário`)

      // Log detalhado dos dados brutos retornados
      console.log('🔍 [Auth] Dados brutos das contas:', accountUsers.map(u => ({
        account_id: u.account_id,
        email: u.email,
        name: u.name,
        account_is_null: u.account === null,
        account_data: u.account
      })))

      // Filtrar contas onde account é null (RLS pode ter bloqueado)
      const validAccountUsersWithData = accountUsers.filter(u => u.account !== null)

      if (validAccountUsersWithData.length === 0) {
        console.error('❌ [Auth] RLS bloqueou acesso aos dados das contas')
        console.error('   Total de account_users encontrados:', accountUsers.length)
        console.error('   Contas com dados acessíveis:', validAccountUsersWithData.length)
        console.error('   Possível problema: RLS da tabela accounts está bloqueando o acesso')
        return null
      }

      // Classificar contas: prioritárias (mesma agência) e outras — só contas ativas
      const accountsInCurrentAgency = validAccountUsersWithData.filter(u => {
        const account = u.account as any
        return account?.agency_id === agency.id && account?.is_active !== false
      })

      const accountsInOtherAgencies = validAccountUsersWithData.filter(u => {
        const account = u.account as any
        return account?.agency_id !== agency.id
      })

      console.log('📊 [Auth] Análise de contas:', {
        total: validAccountUsersWithData.length,
        mesma_agencia: accountsInCurrentAgency.length,
        outras_agencias: accountsInOtherAgencies.length,
        current_agency_id: agency.id
      })

      if (accountsInCurrentAgency.length === 0) {
        console.log('❌ [Auth] Nenhuma conta encontrada para o usuário nesta agência')
        return null
      }

      const validAccountUsers = accountsInCurrentAgency

      console.log(`✅ [Auth] Usando ${validAccountUsers.length} conta(s) na agência atual`)

      // Preparar lista de contas disponíveis para armazenar na sessão
      const availableAccounts = validAccountUsers.map(u => u.account as any)

      console.log('📦 [Auth] Preparando lista de contas disponíveis:', {
        total: availableAccounts.length,
        accounts: availableAccounts.map(acc => ({ id: acc.id, nome: acc.nome }))
      })

      let accountUser = validAccountUsers[0]

      if (validAccountUsers.length > 1) {
        const storedSession = getUserSession()
        const preferredAccountId = storedSession?.user?.account_id

        if (preferredAccountId) {
          const preferred = validAccountUsers.find(u => u.account_id === preferredAccountId)
          if (preferred) {
            accountUser = preferred
            console.log('✅ [Auth] Restaurando conta preferida do localStorage:', preferredAccountId)
          }
        }

        console.log('📋 [Auth] Usuário tem múltiplas contas nesta agência:')
        validAccountUsers.forEach((u, idx) => {
          const acc = u.account as any
          const isSelected = u.account_id === accountUser.account_id
          console.log(`   ${idx + 1}. Conta: ${acc?.nome} (ID: ${u.account_id})${isSelected ? ' ⭐ SELECIONADA' : ''}`)
        })
      }

      const account = accountUser.account as any

      const agencyMatch = account.agency_id === agency.id
      console.log('✅ [Auth] Conta selecionada:', {
        account_id: accountUser.account_id,
        account_name: account.nome,
        account_agency_id: account.agency_id,
        current_agency_id: agency.id,
        domain: agency.domain,
        agency_match: agencyMatch
      })

      const userData = {
        id: accountUser.id,
        email: accountUser.email,
        name: accountUser.name,
        phone: accountUser.phone,
        profile: accountUser.profile,
        account_id: accountUser.account_id,
        account: account,
        auth_user_id: authUserId,
        type: 'account_user' as const,
        role: accountUser.role,
        view_all_contacts: accountUser.view_all_contacts,
        can_assign_contacts: accountUser.can_assign_contacts,
        view_unassigned_contacts: accountUser.view_unassigned_contacts
      }

      console.log('✅ [Auth] Dados do account_user preparados:', {
        id: userData.id,
        email: userData.email,
        name: userData.name,
        type: userData.type,
        profile: userData.profile,
        account_id: userData.account_id,
        has_role: !!userData.role,
        available_accounts: availableAccounts.length
      })

      return { userData, availableAccounts }
    } catch (err) {
      console.error('❌ [Auth] Erro ao buscar dados do usuário:', err)
      return null
    }
  }

  /**
   * Realiza login usando Supabase Auth nativo
   */
  const login = async (email: string, password: string): Promise<boolean> => {
    setError(null)
    setLoading(true)

    console.log('🚀 [Auth] Iniciando processo de login...')

    if (!agency) {
      setError(i18n.t('agency:errors.notIdentified'))
      setLoading(false)
      return false
    }

    if (!supabase) {
      setError(i18n.t('common:errors.notConfigured'))
      setLoading(false)
      return false
    }

    try {
      console.log('📤 [Auth] Autenticando com Supabase Auth...', {
        email,
        agency: agency.company_name
      })

      // Autenticar usando Supabase Auth nativo
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (signInError) {
        console.log('❌ [Auth] Erro no login:', signInError.message)

        // Traduzir erros comuns do Supabase
        let errorMessage = i18n.t('agency:errors.invalidCredentials')
        if (signInError.message.includes('Invalid login credentials')) {
          errorMessage = 'Email ou senha inválidos'
        } else if (signInError.message.includes('Email not confirmed')) {
          errorMessage = 'Email não confirmado'
        } else if (signInError.message.includes('User not found')) {
          errorMessage = 'Usuário não encontrado'
        }

        setError(errorMessage)
        setLoading(false)
        return false
      }

      if (!data.session) {
        console.log('❌ [Auth] Sessão não criada')
        setError(i18n.t('agency:errors.createSessionError'))
        setLoading(false)
        return false
      }

      console.log('✅ [Auth] Autenticação bem-sucedida no Supabase Auth:', {
        user_id: data.user.id,
        email: data.user.email
      })

      // Buscar dados completos do usuário (já filtra por agency_id internamente)
      const fetchResult = await fetchUserData(data.user.id, data.user.email || email)

      if (!fetchResult) {
        console.log('❌ [Auth] Usuário não encontrado ou não pertence a esta agência')
        setError(i18n.t('agency:errors.invalidCredentials'))
        await supabase.auth.signOut()
        setLoading(false)
        return false
      }

      const { userData, availableAccounts } = fetchResult

      const userAgencyIdAtLogin = userData.type === 'agency_user' ? (userData as any).agency_id : userData.account?.agency_id
      const isAgencyMatchAtLogin = userAgencyIdAtLogin === agency.id

      console.log('✅ [Auth] Dados do usuário carregados:', {
        id: userData.id,
        name: userData.name,
        type: userData.type,
        account_id: userData.account_id,
        user_agency_id: userAgencyIdAtLogin,
        current_agency: agency.company_name,
        current_agency_id: agency.id,
        domain: agency.domain,
        agency_match: isAgencyMatchAtLogin,
        status: isAgencyMatchAtLogin ? '✅ AGÊNCIA CORRESPONDENTE' : '⚠️ AGÊNCIA DIFERENTE',
        available_accounts: availableAccounts?.length || 0
      })

      // Para account_users, sincronizar conta ativa no banco
      if (userData.type === 'account_user' && userData.account_id) {
        try {
          const { error: rpcError } = await supabase.rpc('set_active_account', {
            p_account_id: userData.account_id
          })
          if (rpcError) {
            console.error('❌ [Auth] Erro ao sincronizar conta ativa no login:', rpcError)
          }
        } catch (err) {
          console.error('❌ [Auth] Erro ao sincronizar conta ativa no login:', err)
        }
      }

      // Para agency_users, criar sessão RLS no banco
      if (userData.type === 'agency_user' && userData.account_id) {
        console.log('🔐 [Auth] Criando sessão RLS para agency_user...')
        try {
          const { error: sessionError } = await supabase.rpc('update_agency_user_session', {
            p_account_id: userData.account_id
          })

          if (sessionError) {
            console.error('❌ [Auth] Erro ao criar sessão RLS:', sessionError)
          } else {
            console.log('✅ [Auth] Sessão RLS criada para account_id:', userData.account_id)
          }
        } catch (err) {
          console.error('❌ [Auth] Erro ao criar sessão RLS:', err)
        }
      }

      // Salvar sessão completa no localStorage
      const supabaseSession = {
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
        expires_in: data.session.expires_in || 3600,
        expires_at: data.session.expires_at || Math.floor(Date.now() / 1000) + 3600
      }
      saveUserSession(userData, data.session.access_token, agency.id, supabaseSession, availableAccounts)

      // Definir estado do usuário
      setUser(userData)

      // Identificar usuário no Sentry
      setUserContext({
        id: userData.id,
        email: userData.email,
        name: userData.name,
        accountId: userData.account_id,
        profile: userData.profile
      })

      console.log('✅ [Auth] Login realizado com sucesso')
      console.log('=' .repeat(80))
      console.log('🔐 AUTENTICAÇÃO CONCLUÍDA')
      console.log('=' .repeat(80))
      console.log('✅ USUÁRIO AUTENTICADO VIA SUPABASE AUTH')
      console.log('')
      console.log('👤 Usuário:')
      console.log('   - ID:', userData.id)
      console.log('   - Nome:', userData.name)
      console.log('   - Email:', userData.email)
      console.log('   - Tipo:', userData.type)
      console.log('   - Account ID:', userData.account_id)
      console.log('')
      console.log('🏢 Agência:')
      console.log('   - ID:', agency.id)
      console.log('   - Nome:', agency.company_name)
      console.log('   - Domínio:', agency.domain)
      console.log('')
      console.log('🔒 Sessão Supabase Auth:')
      console.log('   - Access Token: Configurado')
      console.log('   - Refresh Token: Configurado')
      console.log('   - Expira em:', new Date((supabaseSession.expires_at || 0) * 1000).toLocaleString())
      console.log('')
      console.log('=' .repeat(80))
      setLoading(false)
      return true

    } catch (err) {
      console.error('❌ [Auth] Erro ao realizar login:', err)
      setError(i18n.t('common:errors.connection'))
      setLoading(false)
      return false
    }
  }

  /**
   * Limpa todos os portais do DOM antes do logout
   */
  const cleanupPortals = (): void => {
    try {
      const portalRoot = document.getElementById('portal-root')
      if (portalRoot) {
        while (portalRoot.firstChild) {
          portalRoot.removeChild(portalRoot.firstChild)
        }
        console.log('✅ [Auth] Portais limpos com sucesso')
      }
    } catch (err) {
      console.warn('⚠️ [Auth] Erro ao limpar portais:', err)
    }
  }

  /**
   * Realiza logout do usuário
   */
  const logout = async (): Promise<void> => {
    console.log('🚪 [Auth] Fazendo logout e limpando sessão...')

    // Limpar portais antes de fazer logout para evitar erros de DOM
    cleanupPortals()

    // Pequeno delay para garantir que os portais foram removidos
    await new Promise(resolve => setTimeout(resolve, 50))

    if (supabase) {
      await supabase.auth.signOut()
      console.log('✅ [Auth] Sessão do Supabase Auth encerrada')
    }

    clearUserSession()
    clearUserContext()
    setUser(null)
    setError(null)
    console.log('✅ [Auth] Logout realizado com sucesso')
  }

  /**
   * Atualiza atividade do usuário
   */
  const trackUserActivity = (): void => {
    if (user) {
      updateLastActivity()
    }
  }

  // Verificar sessão quando a agência for carregada
  useEffect(() => {
    if (agency) {
      console.log('🔄 [Auth] Agência carregada, verificando sessão armazenada...')
      checkStoredAuth()
    } else {
      console.log('⏳ [Auth] Aguardando carregamento da agência...')
    }
  }, [agency])

  // Listener para mudanças de estado de autenticação do Supabase
  // IMPORTANT: callback must NOT be async to avoid deadlocks with navigator locks
  useEffect(() => {
    if (!supabase || !agency) return

    const agencyId = agency.id

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[Auth] Evento de autenticacao:', event)

      if (event === 'SIGNED_OUT') {
        clearUserSession()
        setUser(null)
        return
      }

      if (event === 'TOKEN_REFRESHED' && session) {
        console.log('[Auth] Token renovado automaticamente pelo Supabase')
        const currentSession = getUserSession()
        if (currentSession) {
          const supabaseSession = {
            access_token: session.access_token,
            refresh_token: session.refresh_token,
            expires_in: session.expires_in || 3600,
            expires_at: session.expires_at || Math.floor(Date.now() / 1000) + 3600
          }
          saveUserSession(currentSession.user, session.access_token, agencyId, supabaseSession)

          if (currentSession.user.type === 'agency_user' && currentSession.user.account_id && supabase) {
            ;(async () => {
              try {
                const { error: rlsError } = await supabase.rpc('update_agency_user_session', {
                  p_account_id: currentSession.user.account_id
                })
                if (rlsError) {
                  console.error('[Auth] Erro ao atualizar sessao RLS:', rlsError)
                }
              } catch (err) {
                console.error('[Auth] Erro ao atualizar sessao RLS:', err)
              }
            })()
          }
        }
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [agency, supabase])

  // AbortError recovery: re-establish session if navigator lock times out
  useEffect(() => {
    if (!supabase) return

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event.reason?.name === 'AbortError') {
        console.warn('[Auth] AbortError detectado (navigator lock timeout), recuperando sessao...')
        event.preventDefault()
        setTimeout(async () => {
          try {
            const { data: { session } } = await supabase!.auth.getSession()
            if (session) {
              console.log('[Auth] Sessao recuperada apos AbortError')
            } else {
              console.warn('[Auth] Nenhuma sessao apos AbortError, recarregando...')
              window.location.reload()
            }
          } catch (err) {
            console.error('[Auth] Falha na recuperacao apos AbortError:', err)
          }
        }, 2000)
      }
    }

    window.addEventListener('unhandledrejection', handleUnhandledRejection)
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [supabase])

  // Activity tracking (throttled) + local session renewal only
  useEffect(() => {
    if (!user) return

    let lastActivityUpdate = 0
    const THROTTLE_MS = 30000

    const handleActivity = () => {
      const now = Date.now()
      if (now - lastActivityUpdate > THROTTLE_MS) {
        lastActivityUpdate = now
        trackUserActivity()
      }
    }

    const activityEvents = ['mousedown', 'keypress', 'scroll', 'touchstart', 'click']
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true })
    })

    const sessionCheckInterval = setInterval(() => {
      if (shouldRefreshSession()) {
        extendUserSession()
      }
    }, 5 * 60 * 1000)

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity)
      })
      clearInterval(sessionCheckInterval)
    }
  }, [user])

  return {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!user,
  }
}